import { Dialog } from "@mui/material";
import intl from "react-intl-universal";
import { TbListDetails, TbX } from "react-icons/tb";

const STATUS_STYLES = {
  success: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  failed: "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300",
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(date);
};

export const BaasWebhookLogsModal = ({
  open,
  onClose,
  webhook = null,
  deliveries = [],
  loading = false,
  loadError = false,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="md"
    PaperProps={{ className: "!rounded-2xl !shadow-card" }}
  >
    <div className="px-5 py-6 sm:px-6">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#e7a73f]/15">
            <TbListDetails className="h-5 w-5 text-[#a66f1d]" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-brand-dark dark:text-gray-100">
              {intl.get("baas.api.webhooks.logsTitle")}
            </h2>
            <p className="truncate text-sm text-gray-500 dark:text-gray-400">
              {webhook?.name || webhook?.url || intl.get("baas.api.webhooks.logsSubtitle")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-outline-icon shrink-0"
          onClick={onClose}
          aria-label={intl.get("words.close")}
        >
          <TbX className="h-4 w-4" />
        </button>
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {intl.get("baas.api.webhooks.logsLoading")}
        </div>
      ) : loadError ? (
        <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {intl.get("baas.api.webhooks.logsUnavailable")}
        </div>
      ) : !deliveries.length ? (
        <div className="rounded-lg border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {intl.get("baas.api.webhooks.logsEmpty")}
        </div>
      ) : (
        <div className="max-h-[60vh] overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="hidden border-b border-gray-100 bg-gray-50/80 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:border-brand-dark-border dark:bg-brand-dark-elevated/40 lg:grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)_minmax(0,0.55fr)_minmax(0,0.45fr)_minmax(0,0.7fr)]">
            <span>{intl.get("baas.api.webhooks.logsDate")}</span>
            <span>{intl.get("baas.api.webhooks.logsEvent")}</span>
            <span>{intl.get("baas.api.webhooks.logsStatus")}</span>
            <span>{intl.get("baas.api.webhooks.logsHttp")}</span>
            <span>{intl.get("baas.api.webhooks.logsCorrelation")}</span>
          </div>

          {deliveries.map((entry) => (
            <div
              key={entry.id}
              className="grid grid-cols-1 gap-2 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-brand-dark-border lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)_minmax(0,0.55fr)_minmax(0,0.45fr)_minmax(0,0.7fr)] lg:items-center"
            >
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.webhooks.logsDate")}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {formatDateTime(entry.deliveredAt || entry.createdAt)}
                </p>
              </div>
              <div className="min-w-0">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.webhooks.logsEvent")}
                </p>
                <code className="block truncate text-sm text-gray-600 dark:text-gray-300">
                  {entry.eventType || "—"}
                </code>
                {entry.errorMessage ? (
                  <p className="mt-0.5 truncate text-[11px] text-red-500 dark:text-red-400">
                    {entry.errorMessage}
                  </p>
                ) : null}
              </div>
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.webhooks.logsStatus")}
                </p>
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    STATUS_STYLES[entry.status] ?? STATUS_STYLES.failed
                  }`}
                >
                  {intl.get(`baas.api.webhooks.deliveryStatus.${entry.status}`) || entry.status}
                </span>
              </div>
              <div>
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.webhooks.logsHttp")}
                </p>
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                  {entry.httpStatusCode ?? "—"}
                </p>
              </div>
              <div className="min-w-0">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
                  {intl.get("baas.api.webhooks.logsCorrelation")}
                </p>
                <code className="block truncate text-xs text-gray-500 dark:text-gray-400">
                  {entry.correlationId || "—"}
                </code>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </Dialog>
);
